// Reusable status badge for quotes. Per spec/14 "Status semantics":
//   lead         → customer contacted, no quote yet (steel/gray)
//   draft        → quote being built, not sent (amber)
//   sent         → quote delivered to customer (blue)
//   follow_up    → past initial send, in nudge cadence (orange)
//   negotiating  → customer responded, back-and-forth in progress (purple)
//   won          → customer accepted (green)
//   lost         → customer declined / went silent (muted, strikethrough)
//   expired      → auto-set after N days no response (muted)
//   imported     → historical bootstrap (navy, frozen total)
//
// Used by the quotes list, pipeline Kanban cards, quote detail header — anywhere
// a QuoteStatus needs to render. Two sizes:
//   sm — tighter pill (table rows)
//   md — default (cards, headers)
//
// Tailwind classes only; palette is project-standard (orange/navy/green/amber/steel/blue/purple).
// Pure presentational — no Firebase reads, no router deps.
import type { QuoteStatus } from '@/shared/crmTypes';

export interface QuoteStatusBadgeProps {
  status: QuoteStatus;
  size?: 'sm' | 'md';
}

export interface QuoteStatusVisual {
  label: string;
  colorClass: string;
}

export const QUOTE_STATUS_META: Record<QuoteStatus, QuoteStatusVisual> = {
  lead: {
    label: 'Lead',
    colorClass: 'bg-steel/15 text-steel',
  },
  draft: {
    label: 'Draft',
    colorClass: 'bg-amber/15 text-amber',
  },
  sent: {
    label: 'Sent',
    colorClass: 'bg-blue/15 text-blue',
  },
  follow_up: {
    label: 'Follow-up',
    colorClass: 'bg-orange/15 text-orange',
  },
  negotiating: {
    label: 'Negotiating',
    colorClass: 'bg-purple/15 text-purple',
  },
  won: {
    label: 'Won',
    colorClass: 'bg-green/15 text-green',
  },
  lost: {
    label: 'Lost',
    colorClass: 'bg-bg text-text-mute line-through',
  },
  expired: {
    label: 'Expired',
    colorClass: 'bg-bg text-text-mute',
  },
  imported: {
    label: 'Imported',
    colorClass: 'bg-navy/10 text-navy',
  },
};

const SIZE_CLASSES: Record<NonNullable<QuoteStatusBadgeProps['size']>, string> = {
  sm: 'text-[10px] px-1.5 py-0.5',
  md: 'text-xs px-2 py-0.5',
};

// Defensive fallback for quotes with a status the enum doesn't cover
// (imported records, partial writes, typos). One bad row would otherwise
// crash the whole list page via React unmount. Mirrors the TierBadge guard.
const UNKNOWN_VISUAL: QuoteStatusVisual = {
  label: 'Unknown',
  colorClass: 'bg-bg text-text-mute border border-line-dark',
};

export function QuoteStatusBadge({ status, size = 'md' }: QuoteStatusBadgeProps) {
  const visual = QUOTE_STATUS_META[status] ?? UNKNOWN_VISUAL;
  const sizeCls = SIZE_CLASSES[size];

  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold whitespace-nowrap ${sizeCls} ${visual.colorClass}`}
      aria-label={`${visual.label} status`}
    >
      {visual.label}
    </span>
  );
}
